import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, ExternalLink, Activity, LayoutGrid, Users } from 'lucide-react'
import type { Tenant } from '@/lib/types'
import { UsageBar } from './UsageBar'
import { SyncStatus } from './SyncStatus'

interface TenantsListProps {
  tenants: Tenant[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  itemsPerPage: number
  totalCount: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (count: number) => void
  onViewUsage: (tenant: Tenant) => void
}

export function TenantsList({
  tenants,
  isLoading,
  currentPage,
  totalPages,
  itemsPerPage,
  totalCount,
  onPageChange,
  onItemsPerPageChange,
  onViewUsage,
}: TenantsListProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-[250px]">Tenant</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[180px]">Usage</TableHead>
              <TableHead>Last Sync</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-full bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-8 w-8 ml-auto bg-muted animate-pulse rounded" /></TableCell>
                </TableRow>
              ))
            ) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  No tenants found matching your criteria.
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant: any) => {
                const usage = tenant.tenantUsageMonthly?.[0] || { projectCount: 0, userCount: 0, storageBytes: 0 }
                const planLimits = tenant.tenantPlan?.planLimits || {}
                const maxProjects = planLimits.maxProjects ?? (tenant.license?.plan === 'enterprise' ? -1 : 5)

                return (
                  <TableRow key={tenant.id} className="group">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">{tenant.name}</span>
                        {tenant.domain && (
                          <a
                            href={`https://${tenant.domain}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mt-0.5"
                          >
                            {tenant.domain}
                            <ExternalLink className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        )}
                        <span className="text-[10px] text-muted-foreground font-mono mt-1">ID: {tenant.id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <SyncStatus date={tenant.syncedAt} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 py-1">
                        {tenant.license ? (
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="w-fit font-normal text-xs px-2 h-5">
                              {tenant.license.plan}
                            </Badge>
                            <span className="text-[10px] font-mono text-muted-foreground truncate w-[100px]" title={tenant.license.licenseKey}>
                              {tenant.license.licenseKey}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">No License</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-2 py-1">
                        <UsageBar
                          current={usage.projectCount}
                          max={maxProjects}
                          label="Projects"
                          icon={LayoutGrid}
                        />
                        <UsageBar
                          current={usage.userCount}
                          max={-1}
                          label="Users"
                          icon={Users}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {new Date(tenant.syncedAt).toLocaleDateString()}
                        <br />
                        {new Date(tenant.syncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => onViewUsage(tenant)}>
                            <Activity className="mr-2 h-4 w-4" />
                            View Usage Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Visit Tenant
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} tenants
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(itemsPerPage)} onValueChange={(v) => onItemsPerPageChange(Number(v))}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* Page numbers */}
                {totalPages <= 7 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => onPageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                ) : (
                  <>
                    {currentPage <= 3 ? (
                      <>
                        {Array.from({ length: 5 }, (_, i) => i + 1).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    ) : currentPage >= totalPages - 2 ? (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(1)} className="cursor-pointer">
                            1
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationEllipsis />
                        {Array.from({ length: 5 }, (_, i) => totalPages - 4 + i).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    ) : (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(1)} className="cursor-pointer">
                            1
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationEllipsis />
                        {Array.from({ length: 3 }, (_, i) => currentPage - 1 + i).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        {Array.from({ length: 5 }, (_, i) => totalPages - 4 + i).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  )
}
